import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  limit,
  increment,
  Timestamp
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Playlist } from '../entities/playlist.model';

type PlaylistSeed = Omit<Playlist, 'id' | 'language'>;

const DEFAULT_PLAYLISTS: PlaylistSeed[] = [
  { name: 'Days of the Week', description: 'Monday through Sunday in your target language.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-01')) },
  { name: 'Colors', description: 'Basic and advanced color vocabulary.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-02')) },
  { name: 'Numbers', description: 'Cardinal and ordinal numbers for everyday use.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-03')) },
  { name: 'Family Members', description: 'Words for parents, siblings, and extended family.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-04')) },
  { name: 'Food & Drink', description: 'Essential vocabulary for meals, restaurants, and cooking.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-05')) },
  { name: 'Animals', description: 'Common animals from pets to wildlife.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-06')) },
  { name: 'Greetings', description: 'How to say hello, goodbye, and everything in between.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-07')) },
  { name: 'Body Parts', description: 'Vocabulary for describing the human body.', createdBy: '', totalLikes: 0, createdAt: Timestamp.fromDate(new Date('2026-01-08')) }
];

@Injectable({ providedIn: 'root' })
export class PlaylistService {
  private firestore = inject(Firestore);

  getPlaylists(language: string): Observable<Playlist[]> {
    const ref = collection(this.firestore, 'playlists');
    const q = query(ref, where('language', '==', language));
    return (collectionData(q, { idField: 'id' }) as Observable<Playlist[]>).pipe(
      map(playlists => playlists.sort((a, b) => b.totalLikes - a.totalLikes))
    );
  }

  async addPlaylist(playlist: Omit<Playlist, 'id'>): Promise<string> {
    const ref = collection(this.firestore, 'playlists');
    const docRef = await addDoc(ref, playlist);
    return docRef.id;
  }

  async updatePlaylist(id: string, updates: Partial<Pick<Playlist, 'name' | 'description' | 'visibility' | 'collaboration'>>): Promise<void> {
    const ref = doc(this.firestore, `playlists/${id}`);
    await updateDoc(ref, updates as any);
  }

  async getPlaylistById(id: string): Promise<Playlist | null> {
    const ref = doc(this.firestore, `playlists/${id}`);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } as Playlist : null;
  }

  async incrementTotalLikes(playlistId: string): Promise<void> {
    const ref = doc(this.firestore, `playlists/${playlistId}`);
    await updateDoc(ref, { totalLikes: increment(1) });
  }

  async decrementTotalLikes(playlistId: string): Promise<void> {
    const ref = doc(this.firestore, `playlists/${playlistId}`);
    await updateDoc(ref, { totalLikes: increment(-1) });
  }

  async incrementSongCount(playlistId: string): Promise<void> {
    const ref = doc(this.firestore, `playlists/${playlistId}`);
    await updateDoc(ref, { songCount: increment(1) });
  }

  async decrementSongCount(playlistId: string): Promise<void> {
    const ref = doc(this.firestore, `playlists/${playlistId}`);
    await updateDoc(ref, { songCount: increment(-1) });
  }

  async deletePlaylist(id: string): Promise<void> {
    const ref = doc(this.firestore, `playlists/${id}`);
    await deleteDoc(ref);
  }

  async seedDefaultPlaylists(language: string): Promise<void> {
    const ref = collection(this.firestore, 'playlists');
    const snap = await getDocs(query(ref, where('language', '==', language), limit(1)));
    if (!snap.empty) return;
    await Promise.all(DEFAULT_PLAYLISTS.map(p => addDoc(ref, { ...p, language, songCount: 0, featured: true })));
  }
}
